/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { ConfirmDialogService } from '@src/app/shared/dialogs/confirmDialog';
import { ImportOption } from '@src/app/modules/importer/import-page/ImportOption';
import { ImportDocNote } from '@src/app/modules/importer/ImportDoc';
import { xeroInterface } from '@src/app/modules/importer/import-page/Import-parser-interface';
import { MatInputModule } from '@angular/material/input';
import { FormDateComponent } from '@src/app/shared/form/form-date/form-date.component';
import { CtlHolderComponent } from '@src/app/shared/form/ctl-holder/ctl-holder.component';
import { CurrentUserService } from '@src/app/modules/user/current-user.service';

/* Duplicated in Xero Server*/
export class XeroTenant {
    tenantId!: string;
    tenantName!: string;
    financialYearEndDay!: number;
    financialYearEndMonth!: number;
    organisationName!: string;
}

class ResponseItem {
    items: unknown[][] = [];
    notes: ImportDocNote[] = [];
}

const xeroExports = [{ id: 'units', name: 'Units' }, { id: 'contacts', name: 'Contacts' },
    { id: 'ar', name: 'Accounts Receivable' },
    { id: 'ap', name: 'Accounts Payable' },
    { id: 'trialBalance', name: 'Trial Balance' },
    { id: 'nominals', name: 'Nominal Accounts'},
    { id: 'suppliers', name: 'Suppliers'},
    { id: 'everything', name: 'All Xero Data For Company'}
]
@Component({
    selector: 'app-xero-connector',
    imports: [FormsModule, MatButtonModule, MatSelectModule, ReactiveFormsModule,
        MatFormFieldModule, MatButtonModule, MatFormFieldModule, MatInputModule,
        MatIconModule, MatButtonModule, MatIconModule, CtlHolderComponent],
    templateUrl: './xero-connector.component.html',
    styleUrl: './xero-connector.component.scss'
})
export class XeroConnectorComponent implements OnChanges {
    @Output() parseWith = new EventEmitter<{items: string[][], notes?: ImportDocNote[]}>()
    @Input() currentParser: ImportOption;
    xeroTenants: XeroTenant[] = [];
    xeroExports = xeroExports;
    http = inject(HttpClient);
    cds = inject(ConfirmDialogService);
    currentUserSvc = inject(CurrentUserService);
    currentXeroTenantId: string;
    currentExportId: string;
    xeroInterface: xeroInterface;
    // asAtDate: string = new Date(new Date().getFullYear() - 1, 11, 31).toISOString().split('T')[0];
    asAtDate = new Date(new Date().getFullYear() - 1, 11, 31);
    dateField = FormDateComponent.make('As At Date', 'asAtDate').setupControl();
    selectedTime: string;
    working = false;

    constructor() {
        this.dateField.setDateValue(this.asAtDate);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.currentParser?.currentValue?.parser?.xeroPath) {
            const path = changes.currentParser?.currentValue?.parser?.xeroPath();
            if (path) {
                this.xeroInterface = changes.currentParser.currentValue.parser.xeroPath();
                this.currentExportId = this.xeroInterface.path
            } else {
                this.currentExportId = undefined;
                this.xeroInterface = undefined;
            }
        }
    }

    /* If we want timepicker...
    private updateUnitTime(): void {
        if (!this.asAtDate || !this.selectedTime) return;

        const [hours, minutes] = this.selectedTime.split(':').map(Number);
        const dateWithTime = new Date(this.asAtDate);
        dateWithTime.setHours(hours);
        dateWithTime.setMinutes(minutes);
        dateWithTime.setSeconds(0);
        dateWithTime.setMilliseconds(0);

        this.unitTime = Math.floor(dateWithTime.getTime() / 1000);
    }
    */

    getDataFromXero() {
        const url = `/xeroApi/${this.currentXeroTenantId}/${this.currentExportId}/${this.currentUserSvc.currentTeam.id}`;
        const xeroPath = this.currentParser.parser.xeroPath();
        const params = xeroPath.needsDate ? {asAtDate: this.dateField.getFormValue()} : {}
        this.working = true;
        this.http.get<ResponseItem>(url, {params}).subscribe(result => {
            let notes : ImportDocNote[];
            setTimeout(() => this.working = false);
            if (result.notes) {
                notes = [];
                result.notes.forEach (n => {
                    notes.push(new ImportDocNote(n));
                })
            }
            console.log(result);
            this.parseWith.emit({items: result.items as string[][], notes});
        }, error => {
            setTimeout(() => this.working = false);
            if (error.message) {
                this.cds.alert('Error', 'Could not get data from Xero - ' + error.message);
            } else {
                this.cds.alert('Error', 'Could not get data from Xero');
            }
        })
    }

    connectToXero() {
        console.log('Getting from Xero');
        interface responseType {success: boolean, consentUrl: string, error: string}
        this.working = true;
        this.http.get<XeroTenant[]>('/xeroApi').subscribe( result => {
            console.log('Xero result', result);
            this.xeroTenants = result;
            setTimeout(() => this.working = false);
        }, failure => {
            console.log('Failure', failure);
            setTimeout(() => this.working = false);
            this.xeroTenants = [];
            if (failure?.error?.notConnected || failure?.status === 401) {
                console.log('Do OAuth');
                this.http.get<responseType>(`/xeroApi/connect/${this.currentUserSvc.currentTeam.id}`).subscribe(result => {
                    console.log('Xero result', result);
                    window.location.href = result.consentUrl;
                }, error => {
                    console.warn('Xero error', error);
                    this.cds.alert('Error', 'Could not connect to Xero');
                });

            } else {
                this.cds.alert('Error', 'Xero service not currently available')
            }
        });
    }

}
